'use client';

import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { BlogCardSkeleton } from './blog-cart-skeleton';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';

const ALL_TAB_VALUE = '__all__';

export function BlogsList({
  categories,
  locale,
  excludeBlogId,
}: {
  categories: BlogCategory[];
  locale: string;
  excludeBlogId?: string | number;
}) {
  const { t } = useTranslations();

  // Stable internal tab value so the first tab is always active on initial load
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB_VALUE);

  // Use category id as tab value (stable), not the name (can change / collide)
  const activeCategoryId = activeTab === ALL_TAB_VALUE ? undefined : activeTab;

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeCategoryId ?? ALL_TAB_VALUE, locale, excludeBlogId ?? 'none'],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 6,
          order: 'desc',
          orderBy: 'created_at',
          where: activeCategoryId ? { categories: { equals: activeCategoryId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const isLoading = blogsQuery.isLoading;

  const blogs = useMemo(() => {
    const items = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
    if (excludeBlogId == null) return items;
    return items.filter((b: any) => b?.id !== excludeBlogId);
  }, [blogsQuery.data, excludeBlogId]);

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-8 pt-12 sm:gap-10 sm:pb-10 sm:pt-16 lg:gap-12 lg:pb-12 lg:pt-24',
        'pr-0 sm:pr-4',
      )}
    >
      <Tabs
        value={activeTab}
        defaultValue={ALL_TAB_VALUE}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-start justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0 md:items-center md:justify-center lg:gap-2',
          )}
        >
          <TabsTrigger
            value={ALL_TAB_VALUE}
            className={cn(
              'ea-blogs__tabs-trigger',
              'h-[41px] rounded-[4px] px-[18px] text-sm font-semibold uppercase',
            )}
          >
            {t('text1947')}
          </TabsTrigger>

          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-blogs__tabs-trigger',
                'h-[41px] rounded-[4px] px-[18px] text-sm font-semibold uppercase',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value={activeTab}
          className="grid w-full grid-cols-1 gap-x-6 gap-y-8 pr-4 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12"
        >
          {blogsQuery.error && (
            <Paragraph className="col-span-1 pt-8 text-center sm:col-span-2 lg:col-span-3">
              {t('text2364')}
            </Paragraph>
          )}

          {isLoading ? (
            [...Array(9)].map((_, index) => <BlogCardSkeleton key={index} />)
          ) : blogs.length > 0 ? (
            <>
              {blogs.map((blog: any) => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </>
          ) : (
            <Paragraph className="col-span-1 text-center sm:col-span-2 sm:text-lg lg:col-span-3">
              {t('text2376')}
            </Paragraph>
          )}
        </TabsContent>

        <div
          className={cn('flex w-full items-center justify-center', {
            hidden: !blogsQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            isLoading={blogsQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
            variant={'dark'}
          >
            {t('text3209')}
          </Button>
        </div>
      </Tabs>
    </section>
  );
}
